/**
 * bladeSeo — pure builders for per-tool SEO on the compute-blade pages.
 *
 * RouteSeo consumes these to emit a keyword-rich description, Open Graph /
 * Twitter card tags (so a shared tool link previews as the tool, not the
 * generic homepage), and JSON-LD WebApplication structured data (so each free
 * tool is eligible for rich results and is flagged as free). Kept pure + DOM-
 * free so they're unit-testable without a browser.
 */
import type { ComputeBladeDef } from './bladeRegistry'
import { localize } from '@/i18n/localize'

/** Normalize a lang string or legacy isSv boolean into a lang string for localize(). */
function toLang(langOrIsSv: string | boolean): string {
  if (typeof langOrIsSv === 'boolean') return langOrIsSv ? 'sv' : 'en'
  return langOrIsSv
}

const SITE = 'https://beetlesense.ai'

/** Canonical address: the tool's own public subdomain if it has one, else the path. */
export function bladeCanonical(blade: ComputeBladeDef): string {
  return blade.publicDomain ? `https://${blade.publicDomain}/` : `${SITE}${blade.route}`
}

/** SERP/social title — tool name + value prop + brand. */
export function bladeSeoTitle(blade: ComputeBladeDef, langOrIsSv: string | boolean): string {
  const lang = toLang(langOrIsSv)
  return `${localize(lang, { sv: blade.labelSv, en: blade.labelEn })} — ${localize(lang, { sv: blade.subSv, en: blade.subEn })} · BeetleSense`
}

/**
 * Meta description — the UI value prop plus the two CTR-lifting facts these
 * tools share: free, and no login. (The registry's `sub` stays short for cards;
 * this is the longer search-result line.)
 */
export function bladeSeoDescription(blade: ComputeBladeDef, langOrIsSv: string | boolean): string {
  const lang = toLang(langOrIsSv)
  const sub = localize(lang, { sv: blade.subSv, en: blade.subEn })
  const tail = localize(lang, { sv: 'Gratis verktyg från BeetleSense — ingen inloggning.', en: 'Free BeetleSense tool — no login required.' })
  return `${sub}. ${tail}`
}

/**
 * JSON-LD WebApplication for the tool. schema.org/WebApplication with a zero-
 * price Offer + isAccessibleForFree marks it as a free tool for Google.
 */
export function bladeJsonLd(blade: ComputeBladeDef, langOrIsSv: string | boolean): Record<string, unknown> {
  const lang = toLang(langOrIsSv)
  const isSv = lang.toLowerCase().startsWith('sv')
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: localize(lang, { sv: blade.labelSv, en: blade.labelEn }),
    url: bladeCanonical(blade),
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    inLanguage: isSv ? 'sv-SE' : 'en',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'SEK' },
    description: bladeSeoDescription(blade, lang),
    provider: { '@type': 'Organization', name: 'BeetleSense', url: SITE },
  }
}
